import { isElementHidden } from './readingAnchor.js';

// Mark.js never counts the text inside these elements, so neither may the offsets handed to it.
// Matched as a selector, the way Mark.js does: EPUB chapters are XHTML, where `tagName` is lowercase.
const UNCOUNTED_TEXT_PARENTS_SELECTOR = 'script, style, title, head, html';

/**
 * Resolves the element that owns a node: the node itself when it is an element,
 * otherwise its parent (text nodes never own a CSS selector or a Mark.js context).
 * @param {Node|null|undefined} node
 * @returns {Element|null}
 */
export function findEnclosingElement(node) {
    if (!node) return null;
    return node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
}

/**
 * Names the elements that anchor a range's boundaries, the pair a selection is persisted against.
 * @param {Range} range
 * @returns {{ startParent: Element|null, endParent: Element|null }}
 */
export function findRangeParentElements(range) {
    return {
        startParent: findEnclosingElement(range.startContainer),
        endParent: findEnclosingElement(range.endContainer),
    };
}

/**
 * Sums the text of the elements Mark.js skips that falls inside a measured range.
 * Mark.js drops a text node when its parent matches the exclusion list; those elements never nest
 * other markup, so the text content of the part of each that overlaps the range is the same sum.
 * @param {Element} element - Root the excluded elements are searched under
 * @param {Range} measured - Range whose text is being counted
 * @returns {number}
 */
function countUncountedTextWithin(element, measured) {
    let uncounted = 0;
    for (const excludedElement of element.querySelectorAll(UNCOUNTED_TEXT_PARENTS_SELECTOR)) {
        if (!measured.intersectsNode(excludedElement)) continue;
        const overlap = measured.cloneRange();
        overlap.selectNodeContents(excludedElement);
        if (overlap.compareBoundaryPoints(Range.START_TO_START, measured) < 0) {
            overlap.setStart(measured.startContainer, measured.startOffset);
        }
        if (overlap.compareBoundaryPoints(Range.END_TO_END, measured) > 0) {
            overlap.setEnd(measured.endContainer, measured.endOffset);
        }
        uncounted += overlap.toString().length;
    }
    return uncounted;
}

/**
 * Counts the text characters from the start of an element up to a boundary point inside it,
 * the way Mark.js counts when that element is its context: text under `<script>`, `<style>`
 * and the other elements Mark.js skips is not counted, so an offset measured here addresses
 * the same character Mark.js will. The boundary takes either shape a `Range` boundary has: a
 * text node with a character offset, or an element with a child index.
 * @param {Element} element - Ancestor of the boundary
 * @param {Node} boundaryNode - Boundary container
 * @param {number} [boundaryOffset=0] - Character offset in a text node, child index in an element
 * @returns {number} Characters before the boundary
 */
export function countTextBefore(element, boundaryNode, boundaryOffset = 0) {
    const measured = element.ownerDocument.createRange();
    measured.setStart(element, 0);
    measured.setEnd(boundaryNode, boundaryOffset);
    return measured.toString().length - countUncountedTextWithin(element, measured);
}

/**
 * Collapses the whitespace runs source markup leaves between and inside blocks: newline plus
 * indentation becomes one newline, runs of spaces one space. NBSP is left alone, it is typography.
 * @param {string} text
 * @returns {string}
 */
export function normalizeWhitespace(text) {
    return text.replace(/[ \t]*\n[ \t\r\n]*/g, '\n').replace(/[ \t]{2,}/g, ' ');
}

/**
 * Tells whether a text node sits under an element the reader does not show.
 * @param {Text} textNode
 * @param {Element} root - Ancestor the check stops at, exclusive
 * @returns {boolean}
 */
function isTextHiddenBelowRoot(textNode, root) {
    const view = root.ownerDocument.defaultView;
    for (let element = textNode.parentElement; element && element !== root; element = element.parentElement) {
        if (isElementHidden(view, element)) return true;
    }
    return false;
}

/**
 * Reads the text a range covers from the DOM rather than from the rendered layout.
 * `Selection.toString()` returns rendered text, and CSS drops the trailing space of every
 * line box — in fixed-layout EPUBs each word is its own absolutely positioned block, so the
 * words come back glued together. The DOM text keeps the spaces. Text the reader never shows
 * (`display: none`, such as the hidden `<aside>` of an EPUB footnote) is left out, as the
 * rendered text leaves it out, and the source's whitespace runs are collapsed.
 * @param {Range} range
 * @returns {string}
 */
export function readRangeText(range) {
    const root = findEnclosingElement(range.commonAncestorContainer);
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, node => {
        if (!range.intersectsNode(node)) return NodeFilter.FILTER_REJECT;
        return node.nodeType === Node.TEXT_NODE ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
    });
    let text = '';
    let textNode;

    while ((textNode = walker.nextNode())) {
        if (isTextHiddenBelowRoot(textNode, root)) continue;
        const from = textNode === range.startContainer ? range.startOffset : 0;
        const to = textNode === range.endContainer ? range.endOffset : textNode.length;
        text += textNode.data.slice(from, to);
    }

    return normalizeWhitespace(text);
}
